import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BadgePercent,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  ImageIcon,
  LoaderCircle,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Tag,
  Trash2,
  UploadCloud,
  UsersRound,
  X,
} from 'lucide-react';
import { Promotion, PromotionAudience, PromotionMutationRequest } from '../models/Promotion';
import { PromotionService } from '../services/promotionService';

const MAX_PROMOTION_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_PROMOTION_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/gif',
  'image/webp',
]);
const formControlClassName = 'mt-1.5 block h-11 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100';

interface PromotionFormState {
  tag: string;
  title: string;
  code: string;
  imageUrl: string;
  validFrom: string;
  validUntil: string;
  audience: PromotionAudience;
  sortOrder: number;
  isActive: boolean;
}

const emptyForm: PromotionFormState = {
  tag: '',
  title: '',
  code: '',
  imageUrl: '',
  validFrom: '',
  validUntil: '',
  audience: 'service_user',
  sortOrder: 0,
  isActive: true,
};

const toDateTimeLocal = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const formatDateTime = (value: string | null): string => {
  if (!value) return 'No limit';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleString();
};

export const Promotions: React.FC = () => {
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [audienceFilter, setAudienceFilter] = useState<'all' | 'service_user' | 'all_users'>('all');
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [form, setForm] = useState<PromotionFormState>(emptyForm);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const fetchPromotions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await PromotionService.getPromotions();
      setPromotions(response.data.promotions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch promotions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  useEffect(() => () => {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
  }, [imagePreviewUrl]);

  const resetSelectedImage = () => {
    setSelectedImage(null);
    setImagePreviewUrl(null);
  };

  const openCreate = () => {
    setEditingPromotion(null);
    setForm(emptyForm);
    resetSelectedImage();
    setError(null);
    setIsModalOpen(true);
  };

  const openEdit = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    setForm({
      tag: promotion.tag,
      title: promotion.title,
      code: promotion.code || '',
      imageUrl: promotion.imageUrl || '',
      validFrom: toDateTimeLocal(promotion.validFrom),
      validUntil: toDateTimeLocal(promotion.validUntil),
      audience: promotion.audience,
      sortOrder: promotion.sortOrder,
      isActive: promotion.isActive,
    });
    resetSelectedImage();
    setError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditingPromotion(null);
    setForm(emptyForm);
    resetSelectedImage();
  };

  const updateField = <K extends keyof PromotionFormState>(field: K, value: PromotionFormState[K]) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  const handleImageSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setError(null);

    if (!file) {
      resetSelectedImage();
      return;
    }
    if (!ACCEPTED_PROMOTION_IMAGE_TYPES.has(file.type)) {
      event.target.value = '';
      resetSelectedImage();
      setError('Choose a JPG, PNG, HEIC, HEIF, GIF, or WebP image');
      return;
    }
    if (file.size > MAX_PROMOTION_IMAGE_BYTES) {
      event.target.value = '';
      resetSelectedImage();
      setError('Promotion images must be 5 MB or smaller');
      return;
    }

    setSelectedImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const buildPayload = (): PromotionMutationRequest => ({
    tag: form.tag.trim(),
    title: form.title.trim(),
    code: form.code.trim() || null,
    imageUrl: form.imageUrl.trim() || null,
    validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : null,
    validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : null,
    audience: form.audience,
    sortOrder: form.sortOrder,
    isActive: form.isActive,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.tag.trim() || !form.title.trim()) {
      setError('Tag and title are required');
      return;
    }
    if (form.validFrom && form.validUntil && new Date(form.validUntil) < new Date(form.validFrom)) {
      setError('Valid until must be on or after valid from');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload = buildPayload();
      if (selectedImage) {
        const uploadResponse = await PromotionService.uploadPromotionImage(selectedImage);
        const uploadedImageUrl = uploadResponse.data.cdnUrl || uploadResponse.data.url;
        if (!uploadedImageUrl) throw new Error('The image uploaded, but no image URL was returned');
        payload.imageUrl = uploadedImageUrl;
      }
      const response = editingPromotion
        ? await PromotionService.updatePromotion(editingPromotion.id, payload)
        : await PromotionService.createPromotion(payload);
      setSuccess(response.message);
      setTimeout(() => setSuccess(null), 5000);
      setIsModalOpen(false);
      setEditingPromotion(null);
      setForm(emptyForm);
      resetSelectedImage();
      await fetchPromotions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promotion: Promotion) => {
    if (!window.confirm(`Delete promotion "${promotion.title}"?`)) return;

    try {
      setError(null);
      const response = await PromotionService.deletePromotion(promotion.id);
      setSuccess(response.message);
      setTimeout(() => setSuccess(null), 5000);
      await fetchPromotions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete promotion');
    }
  };

  const activePromotions = promotions.filter(promotion => promotion.isActive).length;
  const serviceUserPromotions = promotions.filter(promotion => promotion.audience === 'service_user').length;
  const activeFilterCount = [Boolean(searchTerm.trim()), statusFilter !== 'all', audienceFilter !== 'all'].filter(Boolean).length;
  const filteredPromotions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return promotions.filter(promotion => {
      const matchesSearch = !normalizedSearch || [promotion.title, promotion.tag, promotion.code || ''].some(value => value.toLowerCase().includes(normalizedSearch));
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? promotion.isActive : !promotion.isActive);
      const matchesAudience = audienceFilter === 'all' || (audienceFilter === 'service_user' ? promotion.audience === 'service_user' : promotion.audience === 'all');
      return matchesSearch && matchesStatus && matchesAudience;
    });
  }, [audienceFilter, promotions, searchTerm, statusFilter]);

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 px-5 py-6 shadow-lg shadow-slate-950/10 sm:px-6">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" aria-hidden="true" />
              Mobile campaign operations
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Promotion Management</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Create and manage promotional banners shown in the service-user mobile application.</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={fetchPromotions}
              disabled={loading}
              className="inline-flex h-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
              Refresh
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Create Promotion
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" role="list" aria-label="Promotion summary">
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><BadgePercent className="h-5 w-5" aria-hidden="true" /></span>
            <div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Total promotions</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : promotions.length.toLocaleString()}</p><p className="mt-1 text-xs text-slate-500">Configured campaigns</p></div>
          </div>
        </article>
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-5 w-5" aria-hidden="true" /></span>
            <div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Active</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : activePromotions.toLocaleString()}</p><p className="mt-1 text-xs text-slate-500">Eligible for display</p></div>
          </div>
        </article>
        <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" role="listitem">
          <div className="flex flex-row-reverse items-start justify-between gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><UsersRound className="h-5 w-5" aria-hidden="true" /></span>
            <div className="mr-auto"><p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Service users</p><p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{loading ? '…' : serviceUserPromotions.toLocaleString()}</p><p className="mt-1 text-xs text-slate-500">Audience-specific campaigns</p></div>
          </div>
        </article>
      </div>

      {success && <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800 shadow-sm" role="status"><CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />{success}</div>}
      {error && <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 shadow-sm" role="alert"><CircleAlert className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" /><div><p className="font-semibold text-red-900">Promotion action failed</p><p className="mt-1">{error}</p></div></div>}

      <div className="admin-table-shell">
        <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><BadgePercent className="h-[18px] w-[18px]" aria-hidden="true" /></span><div><h2 className="text-base font-semibold text-slate-950">Promotion directory</h2><p className="mt-0.5 text-xs text-slate-500" aria-live="polite">Showing {filteredPromotions.length} of {promotions.length} campaigns</p></div></div>
          <button type="button" onClick={() => setAreFiltersOpen(current => !current)} aria-expanded={areFiltersOpen} aria-controls="promotion-filters" className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areFiltersOpen || activeFilterCount > 0 ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}><SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />Filters{activeFilterCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">{activeFilterCount}</span>}<ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" /></button>
        </div>
        <div id="promotion-filters" hidden={!areFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_13rem_13rem]">
            <div><label htmlFor="promotion-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search promotions</label><div className="relative"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" /><input id="promotion-search" type="search" placeholder="Title, tag, or code" className={`${formControlClassName} mt-0 pl-10`} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} /></div></div>
            <div><label htmlFor="promotion-status" className="mb-1.5 block text-xs font-semibold text-slate-600">Status</label><select id="promotion-status" className={`${formControlClassName} mt-0`} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
            <div><label htmlFor="promotion-audience" className="mb-1.5 block text-xs font-semibold text-slate-600">Audience</label><select id="promotion-audience" className={`${formControlClassName} mt-0`} value={audienceFilter} onChange={(event) => setAudienceFilter(event.target.value as typeof audienceFilter)}><option value="all">All audiences</option><option value="service_user">Service users</option><option value="all_users">All users</option></select></div>
          </div>
          {activeFilterCount > 0 && <div className="mt-4 flex justify-end"><button type="button" onClick={() => { setSearchTerm(''); setStatusFilter('all'); setAudienceFilter('all'); }} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"><X className="mr-2 h-4 w-4" aria-hidden="true" />Clear filters</button></div>}
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14">
            <LoaderCircle className="mb-3 h-8 w-8 animate-spin text-blue-600" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-slate-900">Loading promotions…</h3>
            <p className="mt-1 text-sm text-slate-500">Fetching the latest campaign records.</p>
          </div>
        ) : filteredPromotions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500"><ImageIcon className="h-6 w-6" aria-hidden="true" /></div>
            <h3 className="text-sm font-semibold text-slate-900">{promotions.length === 0 ? 'No promotions configured' : 'No promotions match these filters'}</h3>
            <p className="mt-1 text-sm text-slate-500">{promotions.length === 0 ? 'Create a promotion to publish a mobile campaign banner.' : 'Try adjusting or clearing the current filters.'}</p>
            {promotions.length === 0 && <button type="button" onClick={openCreate} className="mt-4 inline-flex h-10 items-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"><Plus className="mr-2 h-4 w-4" aria-hidden="true" />Create Promotion</button>}
          </div>
        ) : (
          <div className="admin-table-scroll" role="region" aria-label="Promotions data table" tabIndex={0}>
            <table className="admin-data-table min-w-max">
              <thead>
                <tr>
                  {['Banner', 'Code', 'Audience', 'Validity', 'Order', 'Status', 'Actions'].map(label => (
                    <th key={label}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPromotions.map(promotion => (
                  <tr key={promotion.id}>
                    <td>
                      <div className="flex min-w-[260px] items-center gap-3">
                        {promotion.imageUrl ? (
                          <img src={promotion.imageUrl} alt="" className="h-12 w-20 rounded-lg border border-slate-200 object-cover" />
                        ) : (
                          <div className="flex h-12 w-20 items-center justify-center rounded-lg bg-slate-100 text-slate-400"><ImageIcon className="h-5 w-5" aria-hidden="true" /></div>
                        )}
                        <div>
                          <div className="font-semibold text-slate-900">{promotion.title}</div>
                          <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Tag className="h-3 w-3" aria-hidden="true" />{promotion.tag}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-sm text-slate-700">{promotion.code ? <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-xs font-semibold text-slate-700">{promotion.code}</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="text-sm text-slate-700">{promotion.audience === 'all' ? 'All users' : 'Service users'}</td>
                    <td className="text-xs text-slate-600">
                      <div className="flex items-center gap-1.5"><CalendarRange className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" /><span>From: {formatDateTime(promotion.validFrom)}</span></div>
                      <div className="mt-1 pl-5">Until: {formatDateTime(promotion.validUntil)}</div>
                    </td>
                    <td className="text-sm font-semibold text-slate-700">{promotion.sortOrder}</td>
                    <td>
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${promotion.isActive ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                        {promotion.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-sm">
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openEdit(promotion)} className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"><Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />Edit</button>
                        <button type="button" onClick={() => handleDelete(promotion)} className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"><Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/65 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="promotion-dialog-title">
          <div className="max-h-[95vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/30">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">Campaign editor</p>
                <h2 id="promotion-dialog-title" className="mt-1 text-lg font-semibold text-slate-950">{editingPromotion ? 'Edit Promotion' : 'Create Promotion'}</h2>
              </div>
              <button type="button" onClick={closeModal} disabled={saving} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50" aria-label="Close promotion editor"><X className="h-4 w-4" aria-hidden="true" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5 p-5 sm:p-6">
              {error && (
                <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
                  <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-slate-700">Tag *
                  <input value={form.tag} onChange={event => updateField('tag', event.target.value)} maxLength={100} className={formControlClassName} placeholder="FIRST BOOKING OFFER" required />
                </label>
                <label className="text-sm font-medium text-slate-700">Code
                  <input value={form.code} onChange={event => updateField('code', event.target.value.toUpperCase())} maxLength={50} className={formControlClassName} placeholder="FIRST20" />
                </label>
              </div>
              <label className="block text-sm font-medium text-slate-700">Title *
                <input value={form.title} onChange={event => updateField('title', event.target.value)} maxLength={200} className={formControlClassName} placeholder="Flat 20% off your first service" required />
              </label>
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <div className="grid gap-4 sm:grid-cols-[160px_1fr] sm:items-start">
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    {imagePreviewUrl || form.imageUrl ? (
                      <img
                        src={imagePreviewUrl || form.imageUrl}
                        alt="Promotion banner preview"
                        className="h-28 w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-28 flex-col items-center justify-center gap-2 px-3 text-center text-xs text-slate-400">
                        <ImageIcon className="h-6 w-6" aria-hidden="true" />
                        Banner preview
                      </div>
                    )}
                  </div>
                  <div>
                    <label htmlFor="promotion-image" className="flex items-center gap-2 text-sm font-medium text-slate-700"><UploadCloud className="h-4 w-4 text-blue-700" aria-hidden="true" />Promotion image</label>
                    <input
                      id="promotion-image"
                      type="file"
                      accept="image/jpeg,image/png,image/heic,image/heif,image/gif,image/webp"
                      onChange={handleImageSelection}
                      className="mt-2 block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-blue-700"
                    />
                    <p className="mt-2 text-xs leading-5 text-slate-500">JPG, PNG, HEIC, HEIF, GIF, or WebP up to 5 MB. The selected file is uploaded when you save.</p>
                    {selectedImage && (
                      <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-600">
                        <span className="truncate" title={selectedImage.name}>{selectedImage.name}</span>
                        <button type="button" onClick={resetSelectedImage} className="shrink-0 font-semibold text-red-600 hover:text-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500">Remove</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <label className="block text-sm font-medium text-slate-700">Image URL <span className="font-normal text-slate-500">(optional alternative)</span>
                <input type="url" value={form.imageUrl} onChange={event => updateField('imageUrl', event.target.value)} className={formControlClassName} placeholder="https://example.com/banner.jpg" />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-slate-700">Valid from
                  <input type="datetime-local" value={form.validFrom} onChange={event => updateField('validFrom', event.target.value)} className={formControlClassName} />
                </label>
                <label className="text-sm font-medium text-slate-700">Valid until
                  <input type="datetime-local" value={form.validUntil} onChange={event => updateField('validUntil', event.target.value)} className={formControlClassName} />
                </label>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-slate-700">Audience
                  <select value={form.audience} onChange={event => updateField('audience', event.target.value as PromotionAudience)} className={formControlClassName}>
                    <option value="service_user">Service users</option>
                    <option value="all">All users</option>
                  </select>
                </label>
                <label className="text-sm font-medium text-slate-700">Sort order
                  <input type="number" min={0} value={form.sortOrder} onChange={event => updateField('sortOrder', Number(event.target.value))} className={formControlClassName} />
                </label>
              </div>
              <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3 text-sm font-medium text-slate-700">
                <input type="checkbox" checked={form.isActive} onChange={event => updateField('isActive', event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                Active and eligible for the mobile application
              </label>
              <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
                <button type="button" onClick={closeModal} disabled={saving} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={saving} className="inline-flex h-10 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                  {saving && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                  {saving ? 'Saving…' : editingPromotion ? 'Update Promotion' : 'Create Promotion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
